import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateField } from "../../../reduxSlice/profileSlice";

export const validateStep1 = (data) => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const alphabetRegex = /^[A-Za-z\s]+$/;

    if (!data.firstName?.trim()) {
        errors.firstName = "First name is required";
    } else if (!alphabetRegex.test(data.firstName)) {
        errors.firstName = "First name must contain alphabets only";
    } else if (data.firstName.length > 50) {
        errors.firstName = "First name cannot exceed 50 characters";
    } else if (data.firstName.trim().length < 3) {
        errors.firstName = "First name must be at least 3 characters";
    }

    if (data.middleName?.trim()) {
        if (!alphabetRegex.test(data.middleName)) {
            errors.middleName = "Middle name must contain alphabets only";
        } else if (data.middleName.length > 50) {
            errors.middleName = "Middle name cannot exceed 50 characters";
        }
    }

    if (!data.lastName?.trim()) {
        errors.lastName = "Last name is required";
    } else if (!alphabetRegex.test(data.lastName)) {
        errors.lastName = "Last name must contain alphabets only";
    } else if (data.lastName.length > 50) {
        errors.lastName = "Last name cannot exceed 50 characters";
    } else if (data.lastName.trim().length < 3) {
        errors.lastName = "Last name must be at least 3 characters";
    }

    if (!data.email?.trim()) {
        errors.email = "Email is required";
    } else if (!emailRegex.test(data.email)) {
        errors.email = "Invalid email format";
    }

    if (!data.gender) errors.gender = "Gender selection is required";

    const targetDate = data.birthday || data.birthDate;
    if (!targetDate) {
        errors.birthday = "Birth date is required";
    } else {
        const today = new Date();
        const birthDate = new Date(targetDate);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();

        if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        if (age < 21) {
            errors.birthday = "Minimum age requirement is 21 years";
        }
    }

    if (!data.birthTime) {
        errors.birthTime = "Birth time is required";
    } else {
        const timeParts = data.birthTime.split(":");
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);

        if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
            errors.birthTime = "Please enter a valid 24-hour time format (00:00 - 23:59)";
        }
    }

    return errors;
};

const Step1BasicInfo = ({ masters = {} }) => {
    const dispatch = useDispatch();
    const formData = useSelector((state) => state.profile);
    const errors = useSelector((state) => state.profile.errors) || {};

    const genderOptions = masters.Genders && masters.Genders.length > 0
        ? masters.Genders
        : [{ name: "Male", value: "male" }, { name: "Female", value: "female" }];

    const getMaxBirthDateString = () => {
        const targetDate = new Date();
        targetDate.setFullYear(targetDate.getFullYear() - 21);
        return targetDate.toISOString().split("T")[0];
    };

    const formatSafeDateValue = (rawDate) => {
        if (!rawDate) return "";
        return String(rawDate).split(/[ T]/)[0];
    };

    const formatSafeTimeValue = (rawTime) => {
        if (!rawTime && rawTime !== 0) return "";
        let timeStr = String(rawTime);
        const parts = timeStr.split(":");
        if (parts.length >= 2) {
            return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
        }
        if (/^\d{3,4}$/.test(timeStr)) {
            const num = parseInt(timeStr, 10);
            const hours = Math.floor(num / 100);
            const minutes = num % 100;
            return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
        }
        return timeStr;
    };

    const handleInputChange = (field, rawValue) => {
        let finalValue = rawValue;

        if (["firstName", "middleName", "lastName"].includes(field)) {
            if (finalValue.length > 0) {
                if (typeof finalValue === "string") {
                    finalValue = finalValue.replace(/\b\w/g, (char) => char.toUpperCase());
                }
            }
        }

        dispatch(updateField({ field, value: finalValue }));

        const simulatedData = { ...formData, [field]: finalValue };
        if (field === "birthday") {
            dispatch(updateField({ field: "birthDate", value: finalValue }));
            simulatedData.birthDate = finalValue;
        }

        const generatedErrors = validateStep1(simulatedData);

        dispatch(updateField({
            field: "errors",
            value: {
                ...errors,
                [field]: generatedErrors[field] || "",
                ...(field === "birthday" ? { birthday: generatedErrors.birthday || "" } : {})
            }
        }));
    };

    return (
        <div className="form-animate">
            <div className="row">
                {["firstName", "middleName", "lastName"].map((field) => {
                    const isRequiredField = field !== "middleName";

                    return (
                        <div className="col-md-12" key={field}>
                            <div className="inputcontainer mb-3">
                                <h5 className="input_label">
                                    {field.replace("Name", " Name").replace(/^\w/, (c) => c.toUpperCase())}
                                    {isRequiredField && <span className="m_feild">*</span>}
                                </h5>
                                <input
                                    className={`form-control input_style ${errors[field] ? "is-invalid" : ""}`}
                                placeholder={`Enter ${field.replace("Name", " Name")}`}
                                value={formData[field] || ""}
                                onChange={(e) => handleInputChange(field, e.target.value)}
                                />
                                {errors[field] && <div className="text-danger small mt-1">{errors[field]}</div>}
                            </div>
                        </div>
                    );
                })}

                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Email<span className="m_feild">*</span></h5>
                        <input
                            type="email"
                            className={`form-control input_style ${errors.email ? "is-invalid" : ""}`}
                        placeholder="Enter Email"
                        value={formData.email || ""}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        />
                        {errors.email && <div className="text-danger small mt-1">{errors.email}</div>}
                    </div>
                </div>

                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Gender<span className="m_feild">*</span></h5>
                        <select
                            className={`form-select input_style ${errors.gender ? "is-invalid" : ""}`}
                        value={formData.gender?.toLowerCase() || ""}
                        onChange={(e) => handleInputChange("gender", e.target.value)}
                        >
                        <option value="">Select Gender</option>
                        {genderOptions.map((opt, i) => (
                            <option key={i} value={opt.value || opt.name.toLowerCase()}>
                        {opt.name}
                            </option>
                            ))}
                    </select>
                    {errors.gender && <div className="text-danger small mt-1">{errors.gender}</div>}
                </div>
            </div>

            <div className="row">
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Birth Date<span className="m_feild">*</span></h5>
                        <input
                            type="date"
                            max={getMaxBirthDateString()}
                            className={`form-control input_style ${errors.birthday || errors.birthDate ? "is-invalid" : ""}`}
                            value={formatSafeDateValue(formData.birthday || formData.birthDate)}
                            onChange={(e) => handleInputChange("birthday", e.target.value)}
                        />
                        {(errors.birthday || errors.birthDate) && (
                            <div className="text-danger small mt-1">{errors.birthday || errors.birthDate}</div>
                        )}
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Birth Time (24-Hour Format)<span className="m_feild">*</span></h5>
                        <input
                            type="time"
                            step="60"
                            className={`form-control input_style ${errors.birthTime ? "is-invalid" : ""}`}
                            value={formatSafeTimeValue(formData.birthTime)}
                            onChange={(e) => handleInputChange("birthTime", e.target.value)}
                        />
                        {errors.birthTime && <div className="text-danger small mt-1">{errors.birthTime}</div>}
                    </div>
                </div>
            </div>
        </div>
    </div>
);
};

export default Step1BasicInfo;